import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../common/Header.js';
import Sidebar from '../common/Sidebar.js';
import Alert from '../common/Alert.js';
import roleService from '../../services/RoleService.js';
import { Settings  } from "lucide-react";

const EditRole = () => {
  const { roleId } = useParams();
  const [formData, setFormData] = useState({
    role_name: '',
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchRole();
  }, [roleId]);

  const fetchRole = async () => {
    try {
      const response = await roleService.getById(roleId);
      setFormData(response.data);
    } catch (err) {
      Alert.error('Failed to load role data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await roleService.update(roleId, formData);
      Alert.success('Role updated successfully!');
      setTimeout(() => navigate('/roles'), 1500);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to update role');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-50 overflow-hidden">
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header />
          <main className="flex-1 flex items-center justify-center p-4">
            <div className="text-center">
              <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
              <p className="text-sm sm:text-base text-gray-600">Loading role data...</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1850px] mx-auto">
      <Header />
      <div className="flex">
        <Sidebar />
        <div className="flex h-screen bg-light w-full mt-16 sm:mt-0">
          <div className="flex-1 flex flex-col overflow-hidden">
            <main className="flex-1 overflow-auto p-3 sm:p-4 md:p-6">
              <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-4 sm:mb-6">
                  <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                      <Settings className="w-5 h-5 text-secondary" />
                      Edit Role
                  </h1>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="bg-white rounded-sm shadow-sm p-4 sm:p-6 md:p-8 space-y-6 sm:space-y-8">
                  {/* Role Information */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Role Information
                    </h3>
                    <div>
                      <label className="block text-sm font-medium text-secondary mb-2">Role Name <span className='text-danger font-bold'>*</span></label>
                      <input
                        type="text"
                        name="role_name"
                        value={formData.role_name}
                        onChange={handleChange}
                        className="w-full sm:w-1/3 px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                        required
                      />
                      <p className="text-xs sm:text-sm text-gray-600 mt-2">Update the role name as needed</p>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-2">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="flex items-center justify-center gap-2 px-3 sm:px-3 py-2.5 sm:py-2 text-xs sm:text-sm border rounded-sm text-buttonTxt bg-buttonBg hover:text-buttonhoverTxt hover:bg-buttonhoverBg transition-all duration-300 font-medium whitespace-nowrap"
                    >
                      {submitting ? 'Updating...' : 'Update Role'}
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate('/roles')}
                      className="flex items-center justify-center gap-2 px-3 sm:px-3 py-2.5 sm:py-2 text-xs sm:text-sm text-buttonTxt bg-buttonBg hover:text-buttonhoverTxt hover:bg-buttonhoverBg border rounded-sm transition-all duration-300 font-medium whitespace-nowrap"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditRole;